import { useTranslation } from 'react-i18next';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { ShipmentResponse, ShipmentStatus } from '../../api/types';
import {
  adminUpdateShipmentStatus,
  uploadShipmentDocument,
} from '../../api/endpoints/admin';
import { useMutationHook } from '../../hooks/useMutationHook';
import { DnmRequestFailure } from '../../api/dnmFailure';
import { shipmentStatusLabel } from './shipmentStatusLabel';
import styles from '../../components/domain/FormModal.module.css';
import advanceStyles from './AdvanceStatusModal.module.css';
import { DialogShell } from '../../components/ui/DialogShell';
import { useDialogDismiss, useIsDirty } from '../../components/ui/dialogDismiss';
import { formatRoute } from '../../utils/routeLabel';
import { readinessMissing } from './kycGates';
import { DialogTitleRow } from '../../help/DialogTitleRow';

interface Props {
  shipment: ShipmentResponse;
  targetStatus: ShipmentStatus;
  onClose: () => void;
}

const STATUS_TITLES: Partial<Record<ShipmentStatus, string>> = {
  EN_ROUTE_PICKUP: 'Mark as heading to pickup',
  AT_PICKUP: 'Mark as at pickup',
  LOADED: 'Mark as loaded',
  IN_TRANSIT: 'Mark as in transit (start trip)',
  AT_DROP: 'Mark as at drop',
  DELIVERED: 'Mark as delivered',
  COMPLETED: 'Mark as completed',
};

export function AdvanceStatusModal({ shipment, targetStatus, onClose }: Props) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [lrFile, setLrFile] = useState<File | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [podFile, setPodFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Person to verify when the API refuses trip start for KYC (null when nobody holds the role). */
  const [kycUserId, setKycUserId] = useState<number | null>(null);
  /** True when the refusal is the driver's trip-start gate (KYC + RC + photo, R18): driver-only copy. */
  const [driverRefused, setDriverRefused] = useState(false);

  const invalidateKeys = [
    ['admin', 'shipments', 'detail', shipment.id],
    ['admin', 'shipments', 'tracking', shipment.id],
    ['admin', 'shipments', 'documents', shipment.id],
    ['admin', 'shipments'],
    ['admin', 'dashboard'],
  ];

  const advanceMut = useMutationHook(
    async () => {
      // 1. Upload state-specific documents if provided
      if (targetStatus === 'LOADED') {
        if (lrFile) {
          await uploadShipmentDocument(shipment.id, 'LR_EWAY', lrFile);
        }
        if (photoFile) {
          await uploadShipmentDocument(shipment.id, 'LOADING_PHOTO', photoFile);
        }
      } else if (targetStatus === 'DELIVERED') {
        if (podFile) {
          await uploadShipmentDocument(shipment.id, 'POD', podFile);
        }
      }

      // 2. Advance the shipment status via admin endpoint
      return adminUpdateShipmentStatus(shipment.id, {
        status: targetStatus,
        reason: reason.trim() || undefined,
      });
    },
    {
      successMessage: `Shipment status successfully updated to ${shipmentStatusLabel(targetStatus)}.`,
      invalidateKeys,
      onSuccess: onClose,
      onError: (err) => {
        // Keep the reason next to the button; KYC refusals carry the role copy (dnmFailure.kycRefusalCopy).
        const kyc = err instanceof DnmRequestFailure && err.code === 'KYC_NOT_VERIFIED' ? err : null;
        setError(err.message);
        setKycUserId(kyc?.userId ?? null);
        setDriverRefused(kyc?.role === 'DRIVER' && kyc.userId != null);
      },
    },
  );

  const driverMissing = readinessMissing(shipment.driverReadiness);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setKycUserId(null);
    setDriverRefused(false);

    advanceMut.mutate(undefined);
  };

  const isLoaded = targetStatus === 'LOADED';
  const isDelivered = targetStatus === 'DELIVERED';
  const isLoading = advanceMut.isLoading;

  const title = STATUS_TITLES[targetStatus] || `Update status to ${shipmentStatusLabel(targetStatus)}`;
  const dirty = useIsDirty({ reason, lrFile, photoFile, podFile });
  const dismiss = useDialogDismiss({ dirty, busy: isLoading, onClose });

  return (
    <DialogShell dismiss={dismiss} label={title} scrimClassName={styles.scrim} panelClassName={styles.panel}>
      <DialogTitleRow context="trip-status" title={title}>
        <h3 className={styles.title}>
          {t('shipments.advanceStatusModal.heading', { title, shipmentId: shipment.id })}
        </h3>
      </DialogTitleRow>
      <p className={styles.hint}>
        {t('shipments.advanceStatusModal.route')}
        {formatRoute(shipment)}
        {t('shipments.advanceStatusModal.currentStatus')}
        <strong title={shipment.status}>{shipmentStatusLabel(shipment.status)}</strong>
      </p>

      <form onSubmit={handleSubmit}>
        {/* Documents for LOADED status */}
        {isLoaded && (
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label className={styles.label}>
                {t('shipments.advanceStatusModal.lrLabel')}
                <span style={{ color: 'var(--muted)' }}>{t('shipments.advanceStatusModal.lrHint')}</span>
              </label>
              <input
                className={styles.input}
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setLrFile(e.target.files?.[0] || null)}
              />
            </div>

            <div>
              <label className={styles.label}>
                {t('shipments.advanceStatusModal.photosLabel')}
                <span style={{ color: 'var(--muted)' }}>{t('shipments.advanceStatusModal.optional')}</span>
              </label>
              <input
                className={styles.input}
                type="file"
                accept="image/*"
                onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
        )}

        {/* Document for DELIVERED status */}
        {isDelivered && (
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label className={styles.label}>
                {t('shipments.advanceStatusModal.podLabel')}
                <span style={{ color: 'var(--muted)' }}>{t('shipments.advanceStatusModal.podHint')}</span>
              </label>
              <input
                className={styles.input}
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setPodFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
        )}

        {/* Notes / Reason */}
        <div style={{ marginTop: 14 }}>
          <label className={styles.label}>
            {t('shipments.advanceStatusModal.notesLabel')}
          </label>
          <textarea
            className={styles.textarea}
            placeholder={
              isLoaded
                ? 'e.g. 20 tons loaded at Bhiwandi warehouse gate 2'
                : isDelivered
                ? 'e.g. Delivered and received by store manager'
                : 'Add optional notes or checkpoint details...'
            }
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
        </div>

        {error && (
          <div className={styles.error} role="alert">
            {driverRefused && (
              <strong className={advanceStyles.errorTitle}>
                {t('shipments.advanceStatusModal.driverNotReady', {
                  items: (driverMissing.length > 0 ? driverMissing : ['KYC', 'RC', 'photo']).join(' / '),
                })}
              </strong>
            )}
            {error}
            {kycUserId != null && (
              <div style={{ marginTop: 6 }}>
                <Link
                  to={`/users/${kycUserId}`}
                  state={{ from: `/shipments/${shipment.id}` }}
                  style={{ color: 'var(--brand-red)', fontWeight: 600 }}
                >
                  {driverRefused
                    ? t('shipments.advanceStatusModal.openDriverProfile')
                    : t('shipments.advanceStatusModal.openProfile')}
                </Link>
              </div>
            )}
          </div>
        )}

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={dismiss.requestClose} disabled={isLoading}>
            {t('common.actions.cancel')}
          </button>
          <button type="submit" className={styles.submit} disabled={isLoading}>
            {isLoading ? 'Updating…' : `Confirm & move to ${shipmentStatusLabel(targetStatus).toLowerCase()}`}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}
